import type {
  DefaultContext,
  MutationOptions,
  OperationVariables,
  TypedDocumentNode,
} from "@apollo/client";
import { t } from "@lingui/macro";
import { client } from "@src/services/apollo-client";
import { appStore } from "@src/stores/AppStore";
import type { GraphQLFormattedError } from "graphql";
import { action, makeObservable } from "mobx";
import { BooleanState } from "../../utils/mobx/states/BooleanState";

export class MutationHelper<
  Mutation,
  MutationVariables extends OperationVariables,
> {
  isLoading = new BooleanState(false);

  constructor(
    private document: TypedDocumentNode<Mutation, MutationVariables>,
  ) {
    makeObservable(this);
  }

  @action async mutate(
    variables?: MutationVariables,
    context?: Omit<
      MutationOptions<Mutation, MutationVariables, DefaultContext>,
      "mutation" | "variables"
    >,
    disableErrorToasts = false,
  ): Promise<
    [Mutation, null] | [null, readonly GraphQLFormattedError[] | Error]
  > {
    this.isLoading.on();

    try {
      const { data, errors } = await client.mutate<Mutation, MutationVariables>(
        {
          ...context,
          mutation: this.document,
          variables: variables,
        },
      );

      if (errors !== undefined) {
        !disableErrorToasts &&
          errors.forEach((error) => {
            appStore.UIStore.toast({
              status: "error",
              title: error.message,
            });
          });
        return [null, errors];
      }

      if (!data) {
        const error = new Error(t`Missing data from mutation.`);
        !disableErrorToasts &&
          appStore.UIStore.toast({
            status: "error",
            title: error.message,
          });
        return [null, error];
      }

      return [data, null];
    } catch (e) {
      console.error(e);
      !disableErrorToasts &&
        appStore.UIStore.toast({
          status: "error",
          title: e instanceof Error ? e.message : t`An error occurred`,
        });
      return [null, e as Error];
    } finally {
      this.isLoading.off();
    }
  }
}
